import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  TextField,
  MenuItem,
  Popover
} from '@mui/material';
import Client from '../Client';
import { salesDailyPerformance } from './simpleBiReportsApi';

function thousandsSeparator(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function formatDay(value) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value).slice(-2);
  }

  return String(date.getDate()).padStart(2, '0');
}

class OverviewDailySalesChart extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      client: new Client("none"),
      loading: false,
      period: "mtd",
      periodName: "This Month",
      selectedDay: null,
      popoverAnchor: null,
      dailySales: {
        result: [],
        totalSales: 0,
        totalOrders: 0,
        avgOrder: 0,
        periodStart: "",
        periodEnd: ""
      }
    };

    this.getData = this.getData.bind(this);
    this.selectPeriod = this.selectPeriod.bind(this);
  }

  componentDidMount() {
    this.getData();
  }

  componentDidUpdate(prevProps) {
    if (String(prevProps.location) !== String(this.props.location)) {
      this.getData();
    }
  }

  selectPeriod = (event) => {
    const value = event.target.value;

    this.setState(
      {
        period: value,
        periodName: value === "last_month" ? "Last Month" : "This Month",
        selectedDay: null,
        popoverAnchor: null
      },
      () => this.getData()
    );
  };

  getData() {
    const queryData = {
      period: this.state.period,
      delivery: 2,
      location: this.props.location || 0
    };

    this.setState({ loading: true });

    const data = salesDailyPerformance(this.state.client, queryData);

    if (!data || !Array.isArray(data?.result)) {
      console.error("Daily sales chart API unavailable", {
        endpoint: "salesDailyPerformance",
        payload: queryData,
        response: data
      });

      this.setState({
        loading: false,
        selectedDay: null,
        popoverAnchor: null,
        dailySales: {
          result: [],
          totalSales: 0,
          totalOrders: 0,
          avgOrder: 0,
          periodStart: "",
          periodEnd: ""
        }
      });

      return;
    }

    this.setState({
      loading: false,
      dailySales: data,
      selectedDay: null,
      popoverAnchor: null
    });
  }

  render() {
    const rows = this.state.dailySales?.result || [];
    const totalSales = this.state.dailySales?.totalSales || 0;
    const totalOrders = this.state.dailySales?.totalOrders || 0;
    const selectedDay = this.state.selectedDay;

    const maxSales = rows.reduce((max, row) => {
      const value = Number(row.sales || 0);
      return value > max ? value : max;
    }, 0);

    return (
      <Card
        sx={{
          background: "#f6f4ef",
          overflow: "hidden",
          position: "relative",
          borderRadius: "14px",
          height: "100%"
        }}
      >
        <CardContent
          sx={{
            p: {
              xs: 1.1,
              sm: 1.25
            },
            "&:last-child": {
              pb: {
                xs: 1.1,
                sm: 1.25
              }
            }
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1,
              mb: 1
            }}
          >
            <Box>
              <Typography
                sx={{
                  color: "#1a237e",
                  fontSize: {
                    xs: "11px",
                    sm: "12px"
                  },
                  fontWeight: 800,
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  lineHeight: 1.2
                }}
              >
                Daily Sales
              </Typography>

              <TextField
                select
                variant="standard"
                size="small"
                value={this.state.period}
                onChange={this.selectPeriod}
                sx={{
                  mt: 0.25,
                  minWidth: 105,
                  "& .MuiInputBase-input": {
                    fontSize: {
                      xs: "12px",
                      sm: "12px"
                    },
                    py: 0.1
                  }
                }}
              >
                <MenuItem value="mtd">This Month</MenuItem>
                <MenuItem value="last_month">Last Month</MenuItem>
              </TextField>
            </Box>

            <Box sx={{ textAlign: "right" }}>
              <Typography
                sx={{
                  color: "#1e2833",
                  fontSize: {
                    xs: "15px",
                    sm: "17px",
                    md: "18px"
                  },
                  fontWeight: 800,
                  lineHeight: 1.2
                }}
              >
                {thousandsSeparator(parseInt(totalSales || 0))}
              </Typography>

              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: {
                    xs: "11px",
                    sm: "12px"
                  }
                }}
              >
                {thousandsSeparator(parseInt(totalOrders || 0))} orders
              </Typography>
            </Box>
          </Box>

          {/* Chart */}
          <Box
            sx={{
              height: {
                xs: 112,
                sm: 120,
                md: 120
              },
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              px: 0.25,
              pb: 0.25
            }}
          >
            {/* Bars */}
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                alignItems: "flex-end",
                gap: {
                  xs: 0.25,
                  sm: 0.45
                }
              }}
            >
              {rows.map((row, index) => {
                const sales = Number(row.sales || 0);
                const isSelected = selectedDay?.date === row.date;

                const heightPercent = maxSales > 0
                  ? Math.max((sales / maxSales) * 100, 4)
                  : 0;

                return (
                  <Box
                    key={row.date || index}
                    title={`${row.date}: ${thousandsSeparator(parseInt(sales || 0))}`}
                    onClick={(event) =>
                      this.setState({
                        selectedDay: row,
                        popoverAnchor: event.currentTarget
                      })
                    }
                    sx={{
                      flex: "1 1 0",
                      minWidth: 0,
                      maxWidth: {
                        xs: 22,
                        sm: 26
                      },
                      height: "100%",
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: "center",
                      cursor: "pointer"
                    }}
                  >
                    <Box
                      sx={{
                        width: {
                          xs: "70%",
                          sm: "72%"
                        },
                        minWidth: 3,
                        maxWidth: 16,
                        height: `${heightPercent}%`,
                        minHeight: sales > 0 ? 4 : 0,
                        borderRadius: "6px 6px 2px 2px",
                        backgroundColor: isSelected ? "#0d1b5e" : "#1a237e",
                        opacity: isSelected ? 1 : 0.78
                      }}
                    />
                  </Box>
                );
              })}
            </Box>

            {/* Labels */}
            <Box
              sx={{
                height: 14,
                display: "flex",
                alignItems: "center",
                gap: {
                  xs: 0.25,
                  sm: 0.45
                },
                mt: 0.45
              }}
            >
              {rows.map((row, index) => (
                <Box
                  key={`${row.date || index}-label`}
                  sx={{
                    flex: "1 1 0",
                    minWidth: 0,
                    maxWidth: {
                      xs: 22,
                      sm: 26
                    },
                    textAlign: "center"
                  }}
                >
                  <Typography
                    sx={{
                      color: "text.secondary",
                      fontSize: {
                        xs: "9.5px",
                        sm: "10px"
                      },
                      lineHeight: 1
                    }}
                  >
                    {rows.length > 18 && index % 2 !== 0 ? "" : formatDay(row.date)}
                  </Typography>
                </Box>
              ))}
            </Box>

            {!rows.length && !this.state.loading && (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <Typography
                  sx={{
                    color: "text.secondary",
                    fontSize: {
                      xs: "13px",
                      sm: "14px"
                    }
                  }}
                >
                  No sales data
                </Typography>
              </Box>
            )}
          </Box>

          <Popover
            open={Boolean(this.state.popoverAnchor)}
            anchorEl={this.state.popoverAnchor}
            onClose={() => this.setState({ popoverAnchor: null })}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            PaperProps={{
              sx: {
                p: 1,
                borderRadius: "10px",
                minWidth: 170,
                boxShadow: "0 6px 18px rgba(0,0,0,0.22)"
              }
            }}
          >
            {this.state.selectedDay && (
              <Box>
                <Typography sx={this.detailLabelSx()}>
                  {this.state.selectedDay.date}
                </Typography>

                <Typography sx={this.detailValueSx()}>
                  Sales: {thousandsSeparator(parseInt(this.state.selectedDay.sales || 0))}
                </Typography>

                <Typography sx={this.detailValueSx()}>
                  Orders: {thousandsSeparator(parseInt(this.state.selectedDay.orders || 0))}
                </Typography>

                <Typography sx={this.detailValueSx()}>
                  Avg: {Number(this.state.selectedDay.avgOrder || 0).toFixed(2)}
                </Typography>
              </Box>
            )}
          </Popover>
        </CardContent>

        {this.state.loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(255,255,255,0.55)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <CircularProgress size={26} />
          </Box>
        )}
      </Card>
    );
  }

  detailLabelSx() {
    return {
      color: "#1a237e",
      fontSize: "12px",
      fontWeight: 800,
      textTransform: "uppercase",
      mb: 0.5
    };
  }

  detailValueSx() {
    return {
      color: "#1e2833",
      fontSize: "13px",
      fontWeight: 700,
      lineHeight: 1.45
    };
  }
}

export default OverviewDailySalesChart;
